import {Injectable, inject} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {finalize, shareReplay} from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {environment} from '@environments/environment';
// The action file, not the @app/stores barrel: the barrel re-exports the effects, which
// inject this service.
import {LoginSuccess} from '@app/stores/authentication/authentication.actions';
import {User} from '@app/modules/shared/models';
import {readStoredCurrentUser} from '@app/_helpers/stored-user';
import {normalizePreferences} from '@app/_helpers/navigation-preferences';
import {normalizeNavigation} from '@app/_components/navigation/model/nav-tree';

/** A sign-in the password-change screen hands back to the sign-in page to finish. */
export interface PendingSignIn {
  email: string;
  password: string;
  remember: boolean;
}

/**
 * The menus, the abilities and the actor's preferences, which ride on every login-shaped response. A key the
 * server sent is the truth, even null. A key it left out (it could not build it, or it is an
 * older server) keeps what is stored - but only for the same person in the same seat, so
 * starting or stopping an impersonation can never carry the previous identity's menu.
 * A plain function, not a method: the specs call buildUserData with a stub for `this`.
 */
function copySessionNavigation(response: any, userData: any): void {
  const has = (key: string) => Object.prototype.hasOwnProperty.call(response, key);
  userData.impersonation = response.impersonation?.active ? {
    active: true,
    actor_id: Number(response.impersonation.actor_id),
    actor_name: String(response.impersonation.actor_name || '')
  } : null;
  const stored = readStoredCurrentUser();
  const sameSeat = !!stored && stored.id != null && Number(stored.id) === Number(userData.id)
    && Number(stored.impersonation?.actor_id || 0) === Number(userData.impersonation?.actor_id || 0);
  userData.navigation = has('navigation') ? normalizeNavigation(response.navigation)
    : sameSeat ? normalizeNavigation(stored.navigation) : null;
  const abilities = has('abilities') ? response.abilities : sameSeat ? stored.abilities : null;
  userData.abilities = Array.isArray(abilities) ? abilities.map(String) : [];
  userData.preferences = normalizePreferences(has('preferences') ? response.preferences
    : sameSeat ? stored.preferences : null);
}

@Injectable({providedIn: 'root'})
export class AuthenticationService {
  private http = inject(HttpClient);

  private baseApiUrl = environment.apiUrl;
  private readonly store = inject(Store);
  private accessTokenSubject = new BehaviorSubject<string | null>(null);
  readonly accessToken$ = this.accessTokenSubject.asObservable();
  readonly currentUser = new BehaviorSubject<any>(null);
  private refreshRequest$: Observable<any> | null = null;
  // The credentials a completed password change hands to the sign-in page, which submits
  // them once and forgets them. Held in memory only: never storage, never router state,
  // both of which would keep the password around after the page is gone.
  private pendingSignIn: PendingSignIn | null = null;

  handOffSignIn(credentials: PendingSignIn): void {
    this.pendingSignIn = credentials;
  }

  takePendingSignIn(): PendingSignIn | null {
    const credentials = this.pendingSignIn;
    this.pendingSignIn = null;
    return credentials;
  }

  loginWithId(userId: number) {
    return this.http.post(`${this.baseApiUrl}/api/v1/user/${userId}/loginWithId`, {}, {
      headers: new HttpHeaders().set('X-Snoball-CSRF', '1'),
      withCredentials: true
    });
  }

  endImpersonation(): Observable<any> {
    return this.http.post(`${this.baseApiUrl}/api/v1/auth/impersonation/end`, {}, {
      headers: new HttpHeaders().set('X-Snoball-CSRF', '1'),
      withCredentials: true
    });
  }

  resetPassword(password: string, token: string) {
    return this.http.post(`${this.baseApiUrl}/api/v1/user/resetPassword?token=${token}`, {password}, {
      headers: new HttpHeaders().set('Skip-auth', 'true')
    });
  }

  tokenLogin(token: string) {
    return this.http.post(`${this.baseApiUrl}/api/v1/user/refreshToken`, {token}, {
      headers: new HttpHeaders()
        .set('Skip-auth', 'true')
        .set('X-Snoball-CSRF', '1'),
      withCredentials: true
    });
  }

  oneClickLogin(token: string) {
    return this.http.post(`${this.baseApiUrl}/api/v1/user/oneClickLogin`, {token}, {
      headers: new HttpHeaders().set('Skip-auth', 'true'),
      withCredentials: true
    });
  }

  resetPasswordToken(token: string) {
    return this.http.post(`${this.baseApiUrl}/api/v1/user/resetPasswordTokenValidate`, {token}, {
      headers: new HttpHeaders().set('Skip-auth', 'true')
    });
  }

  /** Postpone a rule-tightening password reset once, for a week; exchanges the reset token. */
  deferPasswordReset(token: string) {
    return this.http.post(`${this.baseApiUrl}/api/v1/user/passwordResetDefer`, {token}, {
      headers: new HttpHeaders().set('Skip-auth', 'true')
    });
  }

  login(email: string, password: string, remember: boolean, turnstileToken?: string): Observable<any> {
    return this.http.post(`${this.baseApiUrl}/api/v1/user/login`, {
      email,
      password,
      remember,
      turnstile_token: turnstileToken
    }, {
      headers: new HttpHeaders().set('Skip-auth', 'true'),
      withCredentials: true
    });
  }

  /**
   * Merge a login-style response into the shape the app stores and reasons about.
   *
   * The server sends the role as top-level role_slug/role_id, but every permission
   * check reads role_user[0].role.slug, so it is rebuilt here. Login, one-click login,
   * session replace and a silent refresh all go through this, which is what keeps a
   * refreshed session from quietly losing the user's role.
   */
  buildUserData(response: any): any {
    const userData: any = {
      ...response.user,
      token: response.token,
    };
    const hasPasswordWarning = Object.prototype.hasOwnProperty.call(
      response,
      'password_expiry_warning'
    );
    if (hasPasswordWarning) {
      userData.password_expiry_warning = response.password_expiry_warning || null;
    } else {
      let storedUser = null;
      try {
        storedUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
      } catch (_) {
        storedUser = null;
      }
      if (storedUser?.password_expiry_warning) {
        userData.password_expiry_warning = storedUser.password_expiry_warning;
      }
    }
    if (response.role_slug) {
      userData.role_slug = response.role_slug;
    }
    // Every login-shaped response carries default_dashboard now; keeping the stored one
    // covers an older server, which sent none on refresh.
    if (response.default_dashboard) {
      userData.default_dashboard = response.default_dashboard;
    } else if (!userData.default_dashboard) {
      try {
        const storedUser = JSON.parse(localStorage.getItem('currentUser') || 'null');
        if (storedUser?.default_dashboard) {
          userData.default_dashboard = storedUser.default_dashboard;
        }
      } catch (_) { /* no usable stored user */ }
    }
    if (response.role_slug) {
      const roleUser = userData.role_user?.[0] || {};
      userData.role_user = [{
        ...roleUser,
        role_id: roleUser.role_id || response.role_id,
        role: {
          ...(roleUser.role || {}),
          slug: response.role_slug
        }
      }];
    }
    copySessionNavigation(response, userData);
    return userData;
  }

  /**
   * Merge fields into the persisted user, never touching the token. For a change to the
   * signed-in person that is not a new session - their saved preferences, say.
   */
  patchPersistedUser(patch: any): boolean {
    const stored = readStoredCurrentUser();
    if (!stored) {
      return false;
    }
    this.persistUser({...stored, ...patch});
    return true;
  }

  /** Persist the user without its access token, which stays in memory only. */
  persistUser(userData: any): void {
    const persistedUser = {...userData};
    delete persistedUser.token;
    localStorage.setItem('currentUser', JSON.stringify(persistedUser));
  }

  setAccessToken(token: string | null): void {
    this.accessTokenSubject.next(token);
  }

  getAccessToken(): string | null {
    return this.accessTokenSubject.value;
  }

  refreshSession(): Observable<any> {
    if (!this.refreshRequest$) {
      this.refreshRequest$ = this.http.post(`${this.baseApiUrl}/api/v1/auth/refresh`, {}, {
        headers: new HttpHeaders()
          .set('Skip-auth', 'true')
          .set('X-Snoball-CSRF', '1'),
        withCredentials: true
      }).pipe(
        finalize(() => this.refreshRequest$ = null),
        shareReplay({bufferSize: 1, refCount: false})
      );
    }
    return this.refreshRequest$;
  }

  /**
   * Take a login-style response (a refresh, a "stay signed in") as the current session: the
   * token in memory, the user persisted without it, and the store told, since the editors and
   * iframes read the token from there. False when the response carries no session.
   */
  applySessionResponse(response: any): boolean {
    if (!response?.success || !response?.token) {
      return false;
    }
    this.setAccessToken(response.token);
    const userData = this.buildUserData(response);
    this.persistUser(userData);
    this.store.dispatch(LoginSuccess({
      user: Object.assign(new User(), userData),
      impersonating: !!response?.impersonation?.active
    }));
    return true;
  }

  logout(): Observable<any> {
    return this.http.post(`${this.baseApiUrl}/api/v1/auth/logout`, {}, {
      headers: new HttpHeaders().set('X-Snoball-CSRF', '1'),
      withCredentials: true
    });
  }

  replaceSession(conflictToken: string): Observable<any> {
    return this.http.post(`${this.baseApiUrl}/api/v1/auth/session/replace`, {
      conflict_token: conflictToken
    }, {
      headers: new HttpHeaders()
        .set('Skip-auth', 'true')
        .set('X-Snoball-CSRF', '1'),
      withCredentials: true
    });
  }

  completeMfa(
    challengeToken: string,
    code: string,
    recovery = false,
    method = 'totp',
    rememberDevice = false,
    linkToken?: string
  ): Observable<any> {
    const endpoint = recovery ? 'recovery' : 'challenge';
    return this.http.post(`${this.baseApiUrl}/api/v1/auth/mfa/${endpoint}`, {
      challenge_token: challengeToken,
      code,
      method,
      remember_device: rememberDevice,
      // The one-click link from the sign-in code email, in place of a typed code.
      ...(linkToken ? {link_token: linkToken} : {})
    }, {
      headers: new HttpHeaders().set('Skip-auth', 'true'),
      withCredentials: true
    });
  }

  enrollTotp(): Observable<any> {
    return this.http.post(`${this.baseApiUrl}/api/v1/auth/mfa/totp/enroll`, {});
  }

  confirmTotp(methodId: number, code: string): Observable<any> {
    return this.http.post(`${this.baseApiUrl}/api/v1/auth/mfa/totp/confirm`, {
      method_id: methodId,
      code
    }, {
      headers: new HttpHeaders().set('Skip-auth-refresh', 'true')
    });
  }

  enrollEmailMfa(): Observable<any> {
    return this.http.post(`${this.baseApiUrl}/api/v1/auth/mfa/email/enroll`, {});
  }

  confirmEmailMfa(methodId: number, code: string): Observable<any> {
    return this.http.post(`${this.baseApiUrl}/api/v1/auth/mfa/email/confirm`, {
      method_id: methodId,
      code
    }, {
      headers: new HttpHeaders().set('Skip-auth-refresh', 'true')
    });
  }

  sendMfaEmail(challengeToken: string): Observable<any> {
    return this.http.post(`${this.baseApiUrl}/api/v1/auth/mfa/email/send`, {
      challenge_token: challengeToken
    }, {headers: new HttpHeaders().set('Skip-auth', 'true')});
  }

  /**
   * Put the two-factor enrolment reminder off for a week. The server allows this once
   * per account and refuses it inside the final days, so the caller must be ready for a
   * 400 with AUTH_MFA_REMINDER_NOT_DEFERRABLE rather than assume it worked.
   */
  deferMfaReminder(): Observable<any> {
    return this.http.post(`${this.baseApiUrl}/api/v1/user/mfa-reminder-defer`, {});
  }

  getEffectivePolicy(): Observable<any> {
    return this.http.get(`${this.baseApiUrl}/api/v1/auth/policy/effective`);
  }

  getSessions(): Observable<any> {
    return this.http.get(`${this.baseApiUrl}/api/v1/auth/sessions`);
  }

  revokeSession(sessionId: number): Observable<any> {
    return this.http.delete(`${this.baseApiUrl}/api/v1/auth/sessions/${sessionId}`);
  }

  revokeOtherSessions(): Observable<any> {
    return this.http.delete(`${this.baseApiUrl}/api/v1/auth/sessions`, {
      headers: new HttpHeaders().set('X-Snoball-CSRF', '1'),
      withCredentials: true
    });
  }

  getTrustedDevices(): Observable<any> {
    return this.http.get(`${this.baseApiUrl}/api/v1/auth/mfa/trusted-devices`);
  }

  revokeTrustedDevice(deviceId: number): Observable<any> {
    return this.http.delete(
      `${this.baseApiUrl}/api/v1/auth/mfa/trusted-devices/${deviceId}`
    );
  }

  revokeAllTrustedDevices(): Observable<any> {
    return this.http.delete(`${this.baseApiUrl}/api/v1/auth/mfa/trusted-devices/all`);
  }

  removeMfa(methodId: number): Observable<any> {
    return this.http.delete(`${this.baseApiUrl}/api/v1/auth/mfa/${methodId}`);
  }

  getPublicPolicy(): Observable<any> {
    return this.http.get(`${this.baseApiUrl}/api/v1/auth/policy/public`, {
      headers: new HttpHeaders().set('Skip-auth', 'true')
    });
  }

  password(user: any) {
    return this.http.post(`${this.baseApiUrl}/api/v1/user/forgotPassword`, user, {
      headers: new HttpHeaders().set('Skip-auth', 'true')
    });
  }
}
